import { useContext } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";

import Navbar from "react-bootstrap/Navbar";
import Nav from "react-bootstrap/Nav";

import { getAuth } from "firebase/auth";
import { StanfordLogo } from "branding/StanfordLogo";
import { useCourseId } from "../../hooks/router/useUrlParams";
import { getAppSplashRoute } from "course/application/general/utils/apputils";
import { useAuthState } from "react-firebase-hooks/auth";
import { useWindowSize } from "react-use-size";
import { FaChevronLeft, FaHome, FaTimes } from "react-icons/fa";
import { NavigationHistoryContext } from "contexts/NavigationContext";
import { useIsMobile } from "utils/general";
import { useTranslateFunction, useTranslationContext } from "contexts/translation/TranslationContext";

const MINIMIZED_BREAKPOINT = 768;
export const LandingNav = ({ applicationType, subpage, alwaysShowHome = false }) => {

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  const courseId = useCourseId()

  return (
    <Navbar
      collapseOnSelect
      expand="sm"
      bg="light"
      variant="light"
      sticky="top"
    >
      <div className="container-fluid">
        <BackHomeButton applicationType={applicationType} subpage={subpage} alwaysShowHome={alwaysShowHome} />
        {/* <Navbar.Brand>
          <Link to="/" className="stanfordRedLink">
            <BrandView />
          </Link>
        </Navbar.Brand> */}

        {/* <ApplicationLink applicationType={applicationType}/> */}
        <Nav className="me-auto ml-1">
          <ApplicationLink applicationType={applicationType} />{" "}
          <SubpageLink subpage={subpage} />
        </Nav>

        <Nav className="ms-auto">
          <ProfileNav />
        </Nav>
      </div>
    </Navbar>
  );
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
const BrandView = () => {
  const windowSize = useWindowSize();
  const isMinimized = windowSize.width < MINIMIZED_BREAKPOINT;
  if (isMinimized) {
    return <>CIP</>
  }
  return <>
    <StanfordLogo style={{ width: 50 }} /> Code in Place
  </>


}

// `alwaysShowHome` is for pages that are destinations rather than steps in a
// flow (the team page, reached from the footer on every page). There the X —
// "close this and go back to whatever you were doing" — is the wrong offer:
// the reader arrived deliberately and wants a way to the front page, not an
// undo. Those pages opt into the home button regardless of history.
const BackHomeButton = ({applicationType, subpage, alwaysShowHome }) => {
  const { targetCourseId } = useParams()
  const {getPreviousLocation} = useContext(NavigationHistoryContext)
  const courseId = useCourseId()

  const navigate = useNavigate()
  const location = useLocation()
  const currPath = location.pathname

  // On application routes the URL's courseId is already the hosting/parent course
  // (e.g. `pai` for /pai/join/pai1/...), so pass it through rather than re-deriving
  // the parent with a hardcoded public/pylatino guess.
  const studentHome = getAppSplashRoute(targetCourseId, "student", courseId)
  const slHome = getAppSplashRoute(targetCourseId, "sl", courseId)
  const appHome = applicationType === "sectionLeader" ? slHome : studentHome

  // Switching between the student and section-leader applications is a plain
  // link, so the app you switched away from can still be sitting on the history
  // stack. Exclude BOTH application splash routes (not just the current one) so
  // "back" returns to wherever you came from before the first application,
  // rather than to the other application you just switched away from.
  const previousLocation = getPreviousLocation([currPath, studentHome, slHome])

  const className = 'btn btn-sm btn-outline-secondary'
  if(subpage) {
    return <Link to={appHome} className={className}>
      <FaChevronLeft />
    </Link>
  }
  if(alwaysShowHome || !previousLocation) {
    return <Link to={`/`} className={className}>
      <FaHome />
    </Link>
  }
  return <button
    className={className}
    onClick={() => navigate(previousLocation)}
  >
    <FaTimes />
  </button>
}

const SubpageLink = ({ subpage }) => {
  const windowSize = useWindowSize();
  const isMinimized = windowSize.width < MINIMIZED_BREAKPOINT;
  const t = useTranslateFunction('application');
  if (!subpage) return <></>
  // if(isMinimized) return <Link to={``} className="nav-link">{subpage}</Link>
  if (isMinimized) return <></>
  return <>
    <span className="nav-link" style={{ paddingLeft: '0px', paddingRight: '0px' }}>/</span>
    <Link to={``} className="nav-link">{t(subpage)}</Link>
  </>
}

const ApplicationLink = ({ applicationType }) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  const windowSize = useWindowSize();
  const isMinimized = useIsMobile()
  const courseId = useCourseId()
  const t = useTranslateFunction('application');
  const { targetCourseId } = useParams()

  const studentMsg = isMinimized ? "Student App" : "Student Application";
  const sectionLeaderMsg = isMinimized ? "Section Lead App" : "Section Leader Application";
  const slOnboardingMsg = isMinimized ? "SL Onboarding" : "Section Leader Onboarding";
  const studentOnboardingMsg = isMinimized ? "Onboarding" : "Student Onboarding";

  if (!applicationType) return <></>
  if (applicationType === "student") {
    return <Link to={getAppSplashRoute(targetCourseId, "student", courseId)} className="nav-link">{t(studentMsg)} </Link>
  }
  if (applicationType === "sectionLeader") {
    return <Link to={getAppSplashRoute(targetCourseId, "sl", courseId)} className="nav-link">{t(sectionLeaderMsg)}</Link>
  }
  if (applicationType === "slOnboarding") {
    return <Link to={`/${courseId}/onboarding/sl`} className="nav-link">{t(slOnboardingMsg)}</Link>
  }
  if (applicationType == "studentOnboarding") {
    return <Link to={`/${courseId}/onboarding/student`} className="nav-link">{t(studentOnboardingMsg)}</Link>
  }
}

export const ProfileNav = () => {
  const windowSize = useWindowSize();
  const isMinimized = windowSize.width < MINIMIZED_BREAKPOINT;
  const auth = getAuth();
  const [user] = useAuthState(auth)
  const {useTranslateFunction} = useTranslationContext();
  const t = useTranslateFunction('landing');
  if (!user) {
    return <></>
  }
  return (
    <>
      {!isMinimized && <span className="d-flex flex-column justify-content-center mr-2">
        {user.displayName}
      </span>}
      <li className="nav-item">
        <button
          className="btn btn-sm btn-outline-secondary my-sm-0"
          onClick={() => {
            auth.signOut();
          }}
        >
          {t("Log Out")}
        </button>
      </li>
    </>
  );
};
